import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Dumbbell, Apple, TrendingUp, Menu, LogOut, User } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const navigate = useNavigate();
  const [userName] = useState('Demo User');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      toast.error('Bitte absolviere zuerst das Quiz');
      navigate('/quiz');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('userId');
    toast.success('Erfolgreich abgemeldet');
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <header className="border-b border-zinc-800 bg-zinc-900/50 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="flex items-center justify-between h-16">
            <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-teal-400">
              ShapeGirls
            </h1>
            <div className="flex items-center gap-4">
              <span className="text-sm text-zinc-400">Hallo, {userName}</span>
              <Button
                data-testid="logout-btn"
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="h-10 px-4 rounded-lg hover:bg-zinc-800"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-2">Willkommen zurück!</h2>
          <p className="text-base text-zinc-400">Bereit für dein heutiges Training?</p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {/* Workout Card */}
          <motion.div
            data-testid="workout-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            onClick={() => navigate('/workout')}
            className="md:col-span-2 bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
                <Dumbbell className="w-6 h-6" />
              </div>
              <span className="text-xs px-3 py-1 rounded-full bg-purple-500/10 text-purple-400 border border-purple-500/20">
                Heute
              </span>
            </div>
            <h3 className="text-2xl font-semibold mb-2">Dein Trainingsplan</h3>
            <p className="text-zinc-400 mb-4">Personalisierte Workouts für diese Woche</p>
            <div className="flex items-center justify-between">
              <div className="text-sm text-zinc-500">7 Tage Plan</div>
              <Button variant="ghost" size="sm" className="text-purple-400 hover:text-purple-300">
                Ansehen →
              </Button>
            </div>
          </motion.div>

          {/* Meal Plan Card */}
          <motion.div
            data-testid="meal-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/meal')}
            className="md:col-span-1 lg:col-span-2 bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-teal-500/10 flex items-center justify-center text-teal-400">
                <Apple className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold mb-2">Ernährungsplan</h3>
            <p className="text-zinc-400 mb-4">Leckere & gesunde Mahlzeiten</p>
            <Button variant="ghost" size="sm" className="text-teal-400 hover:text-teal-300">
              Ansehen →
            </Button>
          </motion.div>

          {/* Progress Card */}
          <motion.div
            data-testid="progress-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            onClick={() => navigate('/progress')}
            className="md:col-span-3 lg:col-span-4 bg-zinc-900/50 backdrop-blur-md border border-zinc-800/50 rounded-2xl p-6 hover:border-purple-500/30 transition-all duration-300 cursor-pointer group"
          >
            <div className="flex items-start justify-between mb-4">
              <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center text-purple-400">
                <TrendingUp className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-2xl font-semibold mb-2">Deine Fortschritte</h3>
            <p className="text-zinc-400 mb-4">Verfolge deine Transformation</p>
            <div className="grid grid-cols-3 gap-4 mt-6">
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">0</div>
                <div className="text-sm text-zinc-400">Trainings</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">0 kg</div>
                <div className="text-sm text-zinc-400">Fortschritt</div>
              </div>
              <div className="bg-zinc-800/50 rounded-lg p-4">
                <div className="text-2xl font-bold text-white">0</div>
                <div className="text-sm text-zinc-400">Tage Streak</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;